<template>
  <v-form v-model="valid" ref="singOnForm">
    <v-container class="pb-0">
      <v-layout column>
        <v-flex v-if="edit">
          <v-layout align-center column>
            <v-avatar :size="170" color="grey lighten-1">
              <img v-if="form.image.url" :src="form.image.url" :alt="form.image.name">
              <v-btn v-else icon disabled>
                <v-icon large>account_circle</v-icon>
              </v-btn>
            </v-avatar>
            <image-input
                accept="image/*"
                @file-update="updateImage"
                :no-title-update="true"
                :show-remove="!!form.image.name"
                round
            ></image-input>
          </v-layout>
        </v-flex>
        <v-flex>
          <v-layout>
            <v-flex>
              <v-text-field
                  v-model="firstName"
                  label="First name"
                  :rules="[v => !!v || 'required']"
                  required
              >
              </v-text-field>
            </v-flex>
            <v-flex>
              <v-text-field
                  v-model="lastName"
                  label="Last name"
                  :rules="[v => !!v || 'required']"
                  required
              >
              </v-text-field>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex v-if="!edit">
          <v-text-field
              v-model="email"
              label="Phone number or email"
              :rules="[v => !!v || 'required']"
              required
          >
          </v-text-field>
        </v-flex>
        <v-flex :class="{'order-xs2': edit}">
          <v-text-field
              v-model="password"
              label="Password"
              :append-icon="password ? show ? 'visibility' : 'visibility_off' : undefined"
              :type="show ? 'text' : 'password'"
              counter
              @click:append="show = !show"
              :rules="[v => !!v || 'required']"
              :hint="edit ? 'Your password is required to authenticate you as the person changing your profile.' : undefined"
              persistent-hint
              required
          ></v-text-field>
        </v-flex>
        <v-flex>
          <p class="v-label theme--light pb-1">Birthday</p>
          <v-layout>
            <v-select
                v-model.number="birthday.year"
                :items="yearOpt"
                :rules="[v => !!v || 'required']"
                label="Year"
            ></v-select>
            <v-select
                v-model.number="birthday.month"
                :items="monthOpt"
                :rules="[v => !!v || 'required']"
                label="Month"
            ></v-select>
            <v-select
                v-model.number="birthday.day"
                :items="dayOpt"
                :rules="[v => !!v || 'required']"
                label="Day"
            ></v-select>
          </v-layout>
        </v-flex>
        <v-flex>
          <p class="v-label theme--light pb-1">Gender</p>
          <v-radio-group v-model="gender" row :rules="[v => !!v || 'required']">
            <v-radio label="Female" value="Female"></v-radio>
            <v-radio label="Male" value="Male"></v-radio>
            <v-radio label="Custom" value="Custom"></v-radio>
          </v-radio-group>
          <v-flex v-if="gender==='Custom'">
            <v-select
                v-model="pronoun"
                :items="pronounItems"
                :rules="[v => !!v || 'required']"
                label="Select your pronoun"
            ></v-select>
            <v-text-field
                v-model="customGender"
                label="Enter your gender (optional)"
            >
            </v-text-field>
          </v-flex>
        </v-flex>
        <div>
          <v-alert :value="!!getRegisterError" outline color="error" icon="warning" style="border-radius: 0.5rem;">
            {{getRegisterError}}
          </v-alert>
        </div>
        <v-flex class="text-xs-center order-xs4">
          <v-btn color="warning" class="mx-0"
                 @click="signOn"
                 :disabled="!edited"
                 round
                 large :loading="isRegisterLoading">
            {{edit? 'save': 'Sign On'}}
          </v-btn>
        </v-flex>
      </v-layout>
    </v-container>
  </v-form>
</template>

<script>
    import {mapActions, mapGetters, mapMutations} from 'vuex'
    import ImageInput from "@/components/ImageInput";
    import {dayInMonth, months, newDate} from "@/services/time";

    export default {
        name: "SignOn",
        components: {ImageInput},
        data() {
            return {
                show: false,
                valid: false,
                firstName: "",
                lastName: "",
                email: "",
                password: "",
                birthday: {
                    year: "",
                    month: "",
                    day: ""
                },
                gender: "",
                customGender: "",
                pronoun: "",
                pronounItems: ["She: \"Wish her a happy birthday!\"",
                    "He: \"Wish him a happy birthday!\"",
                    "They: \"Wish them a happy birthday!\""],
                birthdayMenu: "",
                form: {
                    image: {
                        file: '',
                        name: '',
                        url: ''
                    }
                }
            }
        },
        props: {
            edit: {
                default: false,
                type: Boolean
            }
        },
        computed: {
            ...mapGetters('auth', ['isSuccess', 'getRegisterError', 'isRegisterLoading']),
            edited() {
                return !this.edit ||
                    this.firstName !== this.user.fname ||
                    this.lastName !== this.user.lname ||
                    this.form.image.url !== this.user.photo ||
                    this.birthday.year !== parseInt(this.user.birthyear) ||
                    this.birthday.month !== parseInt(this.user.birthmonth) ||
                    this.birthday.day !== parseInt(this.user.birthday) ||
                    this.gender !== this.user.sex
            },
            user() {
                return this.$store.state.auth.user
            },
            monthOpt() {
                let options = []
                for (let i = 0; i < months.length; i++) {
                    options.push(
                        {value: i + 1, text: months[i]}
                    )
                }
                return options
            },
            dayOpt() {
                let days = []
                for (let i = 1; i <= dayInMonth[this.birthday.month - 1]; i++) {
                    days.push(i)
                }
                return days
            },
            yearOpt() {
                let years = []
                let start = new Date().getFullYear() - 18
                for (let i = 0; i < 100; i++) {
                    years.push(start - i)
                }
                return years
            }
        },
        methods: {
            ...mapActions('auth', ['register', 'updateProfile']),
            ...mapMutations('notification', ['showSnackbar']),
            signOn() {
                if (!this.$refs.singOnForm.validate() || !this.edited || !this.valid || !this.gender) return
                const data = {
                    userid: this.email,
                    password: this.password,
                    fname: this.firstName,
                    lname: this.lastName,
                    birthyear: this.birthday.year,
                    birthmonth: this.birthday.month,
                    birthday: this.birthday.day,
                    sex: this.gender || this.customGender
                }
                if (this.edit) {
                    data["photo"] = this.form.image.url
                    delete data.userid
                    if (data.fname === this.user.fname) delete data.fname
                    if (data.lname === this.user.lname) delete data.lname
                    if (data.photo === this.user.photo) delete data.photo
                    if (data.birthyear === parseInt(this.user.birthyear)) delete data.birthyear
                    if (data.birthmonth === parseInt(this.user.birthmonth)) delete data.birthmonth
                    if (data.birthday === parseInt(this.user.birthday)) delete data.birthday
                    if (data.sex === this.user.sex) delete data.sex

                    this.updateProfile(data).then(() => {
                        if (this.isSuccess) {
                            this.showSnackbar({text: 'Profile updated.', type: 'success'})
                            this.reset()
                            this.$refs.singOnForm.resetValidation()
                            // this.$router.push({name: 'home'})
                        }
                    })
                } else {
                    this.register(data).then(() => {
                        if (this.isSuccess) {
                            let query = Object.assign({}, this.$route.query)
                            delete query.InstaplayId
                            delete query.userid
                            delete query.redirect
                            this.$router.push({name: 'home', query})
                        }
                    })
                }
            },
            updateImage: function (file) {
                if (file) {
                    this.form.image.file = file // this file can be sent to server...
                    this.form.image.name = file.name
                    const fr = new FileReader()
                    fr.readAsDataURL(file)
                    fr.addEventListener('load', () => {
                        this.form.image.url = fr.result
                    })
                } else {
                    this.form.image.url = this.$store.state.auth.user.photo
                    this.form.image.name = ''
                    this.form.image.file = ''
                }
            },
            reset() {
                if (this.edit) {
                    this.firstName = this.user.fname
                    this.lastName = this.user.lname
                    this.password = ''
                    this.birthday.year = this.user.birthyear
                    this.birthday.month = this.user.birthmonth
                    this.birthday.day = this.user.birthday
                    this.gender = this.user.sex
                    this.form.image.url = this.user.photo
                } else {
                    let start = new Date(new Date().setFullYear(new Date().getFullYear() - 18))
                    this.birthday.year = start.getFullYear()
                    this.birthday.month = start.getMonth() + 1
                    this.birthday.day = start.getDate()
                }
            }
        },
        mounted() {
            this.reset()
        }
    }
</script>

<style scoped>

</style>
